import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { leadService } from '../../../services/leadService';
import { contactService } from '../../../services/contactService';
import { followUpService } from '../../../services/followUpService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { ConnectionDetailModal } from '../../../components/app/connections/ConnectionDetailModal';
import { FollowUpModal } from '../../../components/app/followups/FollowUpModal';
import { isPast, isToday, startOfDay } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const ConnectionsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [momentStats, setMomentStats] = useState(new Map());
  const [openByContact, setOpenByContact] = useState(new Map());
  const [tab, setTab] = useState('connections'); // 'connections' | 'submissions'
  const [loading, setLoading] = useState(true);
  const [followUpFor, setFollowUpFor] = useState(null);
  const [success, setSuccess] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterImportant, setFilterImportant] = useState('all'); // 'all' | 'important'
  const [filterFollowUp, setFilterFollowUp] = useState('all'); // 'all' | 'due' | 'overdue' | 'none'
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'oldest' | 'interaction' | 'followup'

  const [selectedLead, setSelectedLead] = useState(null);

  const fetchAll = async () => {
    const data = await leadService.getLeads(user.id);
    setLeads(data);
    const [contactData, stats, open] = await Promise.all([
      contactService.getContacts().catch(() => []),
      contactService.getMomentStats().catch(() => new Map()),
      followUpService.listOpen(user.id).catch(() => [])
    ]);
    setContacts(contactData);
    setMomentStats(stats);
    const byContact = new Map();
    open.forEach(r => {
      if (r.contact_id && !byContact.has(r.contact_id)) byContact.set(r.contact_id, r);
    });
    setOpenByContact(byContact);
  };

  useEffect(() => {
    const run = async () => {
      try {
        await fetchAll();
      } catch (err) {
        console.error('Failed to load connections:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) run();
  }, [user]);

  const handleOpenLead = async (lead) => {
    if (lead.isContactOnly) {
      navigate('/app/moments');
      return;
    }
    setSelectedLead(lead);
    
    if (!lead.is_read) {
      try {
        const updated = await leadService.updateLead(lead.id, user.id, { is_read: true });
        setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
      } catch (err) {
        console.error('Failed to mark read');
      }
    }
  };

  const handleUpdateLead = (updatedLead) => {
    setLeads(prev => prev.map(l => l.id === updatedLead.id ? { ...l, ...updatedLead } : l));
    setSelectedLead(prev => ({ ...prev, ...updatedLead }));
  };

  const handleFollowUpSaved = async (row, text) => {
    setFollowUpFor(null);
    setSuccess(text);
    setTimeout(() => setSuccess(''), 3000);
    try {
      await fetchAll();
    } catch (err) {
      console.error('Failed to refresh connections:', err);
    }
  };

  const checkReminderStatus = (reminder) => {
    if (!reminder) return null;
    const due = startOfDay(new Date(reminder.due_date));
    const today = startOfDay(new Date());
    
    if (isPast(due) && !isToday(due)) return 'overdue';
    if (isToday(due)) return 'today';
    return 'upcoming';
  };

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('all');
    setFilterImportant('all');
    setFilterFollowUp('all');
    setSortBy('newest');
  };

  // A Connection is a Contact with at least one Moment (derived, never stored).
  const contactById = new Map(contacts.map(c => [c.id, c]));
  const leadByContact = new Map();
  leads.forEach(l => {
    if (l.contact_id && !leadByContact.has(l.contact_id)) leadByContact.set(l.contact_id, l);
  });

  const connectionItems = [];
  momentStats.forEach((st, contactId) => {
    const contact = contactById.get(contactId);
    if (!contact) return;
    const lead = leadByContact.get(contactId);
    if (lead) {
      connectionItems.push({ ...lead, momentCount: st.count });
    } else {
      connectionItems.push({
        id: contact.id,
        isContactOnly: true,
        full_name: contact.full_name || 'Unnamed contact',
        email: contact.email || '',
        company_name: contact.company,
        job_title: contact.job_title,
        is_read: true,
        is_archived: false,
        is_important: false,
        status: null,
        tags: [],
        activeReminder: openByContact.get(contact.id) || null,
        created_at: contact.created_at,
        last_interaction_at: st.last,
        momentCount: st.count
      });
    }
  });

  const submissionItems = leads.filter(l => !(l.contact_id && momentStats.has(l.contact_id)));
  const sourceItems = tab === 'connections' ? connectionItems : submissionItems;

  let filteredLeads = sourceItems.filter(l => {
    if (l.is_archived) return false;
    if (filterStatus !== 'all' && l.status !== filterStatus) return false;
    if (filterImportant === 'important' && !l.is_important) return false;
    
    if (filterFollowUp !== 'all') {
      const rStatus = checkReminderStatus(l.activeReminder);
      if (filterFollowUp === 'none' && rStatus) return false;
      if (filterFollowUp === 'due' && rStatus !== 'today' && rStatus !== 'upcoming') return false;
      if (filterFollowUp === 'overdue' && rStatus !== 'overdue') return false;
    }

    if (search) {
      const s = search.toLowerCase();
      const tagMatch = l.tags?.some(t => t.name.toLowerCase().includes(s));
      if (!l.full_name.toLowerCase().includes(s) && 
          !l.email.toLowerCase().includes(s) && 
          !(l.company_name && l.company_name.toLowerCase().includes(s)) &&
          !tagMatch) {
        return false;
      }
    }
    return true;
  });

  filteredLeads.sort((a, b) => {
    if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
    if (sortBy === 'interaction') return new Date(b.last_interaction_at || b.created_at) - new Date(a.last_interaction_at || a.created_at);
    if (sortBy === 'followup') {
      const dateA = a.activeReminder ? new Date(a.activeReminder.due_date).getTime() : Infinity;
      const dateB = b.activeReminder ? new Date(b.activeReminder.due_date).getTime() : Infinity;
      return dateA - dateB;
    }
    return 0;
  });

  const getReminderBadge = (reminder) => {
    const status = checkReminderStatus(reminder);
    if (!status) return null;
    
    if (status === 'overdue') return <span className="flex items-center text-xs font-bold text-red-600 bg-red-50/80 border border-red-100 px-2.5 py-1 rounded-lg"><SafeIcon icon={FiIcons.FiAlertCircle} className="mr-1.5" /> Overdue</span>;
    if (status === 'today') return <span className="flex items-center text-xs font-bold text-amber-600 bg-amber-50/80 border border-amber-100 px-2.5 py-1 rounded-lg"><SafeIcon icon={FiIcons.FiCalendar} className="mr-1.5" /> Due Today</span>;
    return <span className="flex items-center text-xs font-bold text-blue-600 bg-blue-50/80 border border-blue-100 px-2.5 py-1 rounded-lg"><SafeIcon icon={FiIcons.FiCalendar} className="mr-1.5" /> {new Date(reminder.due_date).toLocaleDateString()}</span>;
  };

  if (loading) return <div className="flex justify-center py-32"><Spinner size="lg" /></div>;

  const tabs = [
    { id: 'connections', label: `Connections (${connectionItems.length})` },
    { id: 'submissions', label: `Shared details (${submissionItems.length})` }
  ];

  return (
    <div className="max-w-6xl pb-16">
      {selectedLead && (
        <ConnectionDetailModal 
          lead={selectedLead} 
          user={user} 
          onClose={() => setSelectedLead(null)} 
          onUpdate={handleUpdateLead} 
        />
      )}
      {followUpFor && (
        <FollowUpModal
          person={{
            contactId: followUpFor.isContactOnly ? followUpFor.id : followUpFor.contact_id,
            connectionId: followUpFor.isContactOnly ? null : followUpFor.id,
            name: followUpFor.full_name
          }}
          onClose={() => setFollowUpFor(null)}
          onSaved={handleFollowUpSaved}
        />
      )}

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Connections</h1>
          <p className="mt-2 text-[15px] font-medium text-slate-500">Nurture and track your professional relationships.</p>
        </div>
      </header>

      {success && <Alert variant="success" message={success} className="mb-6" />}

      <div className="flex flex-wrap items-center gap-2 mb-2">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              tab === t.id
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="text-sm font-medium text-slate-500 mb-8">
        {tab === 'connections'
          ? 'Contacts you have shared a Moment with.'
          : 'People who shared their details with you but have no Moment yet.'}
      </p>

      {/* Advanced Filters - Cleaned up form fields */}
      <div className="bg-white p-6 rounded-3xl border border-slate-200/60 shadow-sm mb-8 space-y-5">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input 
              placeholder="Search names, companies, or tags..." 
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-11 m-0 bg-slate-50 border-slate-100 focus:bg-white"
            />
          </div>
          <div className="flex flex-wrap sm:flex-nowrap gap-3">
            <select 
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium focus:ring-4 focus:ring-slate-900/10 focus:border-slate-900 outline-none bg-slate-50 hover:bg-slate-100 transition-colors"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="following_up">Following Up</option>
              <option value="active">Active</option>
              <option value="client">Client</option>
            </select>

            <select 
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium focus:ring-4 focus:ring-slate-900/10 focus:border-slate-900 outline-none bg-slate-50 hover:bg-slate-100 transition-colors"
              value={filterFollowUp}
              onChange={e => setFilterFollowUp(e.target.value)}
            >
              <option value="all">Any Follow-up</option>
              <option value="due">Upcoming / Today</option>
              <option value="overdue">Overdue</option>
              <option value="none">No Follow-up</option>
            </select>

            <select 
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium focus:ring-4 focus:ring-slate-900/10 focus:border-slate-900 outline-none bg-slate-50 hover:bg-slate-100 transition-colors"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="newest">Recently Connected</option>
              <option value="interaction">Last Interacted</option>
              <option value="followup">Follow-up Date</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t border-slate-100/60">
          <label className="flex items-center space-x-2.5 cursor-pointer">
            <input 
              type="checkbox" 
              checked={filterImportant === 'important'}
              onChange={e => setFilterImportant(e.target.checked ? 'important' : 'all')}
              className="rounded-md border-slate-300 text-slate-900 focus:ring-slate-900 w-4 h-4 cursor-pointer" 
            />
            <span className="text-sm font-bold text-slate-700 flex items-center tracking-tight">
              <SafeIcon icon={FiIcons.FiStar} className="mr-1.5 text-amber-500 fill-current" /> Important Only
            </span>
          </label>

          {(search || filterStatus !== 'all' || filterImportant !== 'all' || filterFollowUp !== 'all' || sortBy !== 'newest') && (
            <button 
              onClick={clearFilters}
              className="text-sm text-slate-500 hover:text-slate-900 font-bold transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="bg-white rounded-[2rem] border border-slate-200/60 border-dashed p-16 text-center shadow-sm">
          <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-slate-100">
            <SafeIcon icon={FiIcons.FiUsers} className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3 tracking-tight">No relationships found</h3>
          <p className="text-[15px] font-medium text-slate-500 max-w-sm mx-auto leading-relaxed">
            {sourceItems.length === 0 
              ? 'Share your digital card to start building your network.' 
              : 'Try adjusting your filters or search terms.'}
          </p>
          {tab === 'connections' && sourceItems.length === 0 && (
            <p className="text-sm font-medium text-slate-400 max-w-sm mx-auto mt-3">
              Add a Moment and choose who was there. They will appear here as a connection.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLeads.map(lead => (
            <div 
              key={lead.id} 
              className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-6 flex flex-col hover:shadow-md hover:border-slate-300 transition-all duration-300 relative group cursor-pointer"
              onClick={() => handleOpenLead(lead)}
            >
              {!lead.is_read && <span className="absolute top-6 left-3 w-2.5 h-2.5 rounded-full bg-blue-500" title="New" />}
              
              <div className="flex items-start gap-3 mb-4 pl-2">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-slate-400">
                  {lead.contact_photo_url ? (
                    <img src={lead.contact_photo_url} alt={lead.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold text-slate-400">{lead.full_name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0 pr-3">
                  <h3 className="font-bold text-xl text-slate-900 tracking-tight truncate flex items-center">
                    {lead.full_name} {lead.is_important && <SafeIcon icon={FiIcons.FiStar} className="ml-2 text-amber-400 fill-current shrink-0" />}
                  </h3>
                  {(lead.job_title || lead.company_name) && (
                    <p className="text-sm font-medium text-slate-500 mt-1 truncate">
                      {lead.job_title} {lead.company_name ? `at ${lead.company_name}` : ''}
                    </p>
                  )}
                </div>
              </div>

              {/* Badges & Tags */}
              <div className="pl-3 flex flex-wrap gap-2 mb-6">
                {lead.momentCount > 0 && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100">
                    {lead.momentCount} {lead.momentCount === 1 ? 'moment' : 'moments'}
                  </span>
                )}
                {lead.status && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200/50">
                    {lead.status.replace('_', ' ')}
                  </span>
                )}
                {lead.tags?.slice(0, 2).map(t => (
                  <span key={t.id} className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-slate-50 border border-slate-200 text-slate-500 truncate max-w-[100px]">
                    {t.name}
                  </span>
                ))}
                {lead.tags?.length > 2 && <span className="text-xs font-bold text-slate-400">+{lead.tags.length - 2}</span>}
              </div>

              <div className="space-y-3 flex-1 pl-3 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm font-medium text-slate-600 truncate mr-2">
                    <SafeIcon icon={FiIcons.FiMail} className="mr-2.5 text-slate-400 shrink-0" />
                    <span className="truncate">{lead.email}</span>
                  </div>
                  <a 
                    href={`mailto:${lead.email}`} 
                    className="text-slate-400 hover:text-blue-600 bg-slate-50 hover:bg-blue-50 p-2 rounded-lg transition-colors border border-slate-100"
                    title="Email"
                    onClick={e => e.stopPropagation()}
                  >
                    <SafeIcon icon={FiIcons.FiSend} className="text-sm" />
                  </a>
                </div>
                
                <div className="flex items-center text-xs font-medium text-slate-500 bg-slate-50/80 p-2.5 rounded-xl border border-slate-100/60">
                  <SafeIcon icon={FiIcons.FiClock} className="mr-2 text-slate-400 shrink-0" />
                  Last interaction: <span className="font-bold text-slate-700 ml-1">{lead.last_interaction_at ? new Date(lead.last_interaction_at).toLocaleDateString() : 'Never'}</span>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-4 border-t border-slate-100/60 mt-auto pl-3">
                <div className="flex-1">
                  {getReminderBadge(lead.activeReminder)}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="rounded-xl px-3"
                  title="Add follow-up"
                  onClick={(e) => { e.stopPropagation(); setFollowUpFor(lead); }}
                >
                  <SafeIcon icon={FiIcons.FiPlus} className="mr-1" /> Follow-up
                </Button>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="rounded-xl px-4 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => { e.stopPropagation(); handleOpenLead(lead); }}
                >
                  {lead.isContactOnly ? 'View moments' : 'Manage'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};